import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import Rightbar from 'pages/main/cheques/Rightbar'
import ChainFilterBar, { useChainFilters, FilterField, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, decimal, columnStats, heatStyle, buildRanks, chainColor,
	RankBadge, Delta, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'

// Кто вёз заказ
const CARRIERS = ['own', 'external', 'aggregator', 'no_courier']
const CARRIER_COLORS = { own: '#5b73e8', external: '#7c4dff', aggregator: '#f59f00', no_courier: '#adb5bd' }
// Ключ «кто вёз» в запросе расшифровки
const CARRIER_PARAM = { own: 'own', external: 'external', aggregator: 'aggregator', no_courier: 'none' }

const SLA_DEFAULT = 45
const SLA_MIN = 5
const SLA_MAX = 240
// Курьер с парой доставок — не показатель: в рейтинг идут от этого числа
const MIN_COURIER_DELIVERIES = 5

function clampSla(value) {
	const number = Number(value)
	if (!number || isNaN(number)) return SLA_DEFAULT
	return Math.min(SLA_MAX, Math.max(SLA_MIN, Math.round(number)))
}

function minutes(value) {
	return value === null || value === undefined ? '—' : decimal(value, 1)
}

// Разница в процентных пунктах: доля вовремя
function PointsDelta({ value }) {
	const { t } = useTranslation()
	if (value === null || value === undefined) return null
	const number = Number(value)
	if (Math.abs(number) < 0.05) return <span className="chain-delta flat">0 {t('chain_pp')}</span>
	return (
		<span className={`chain-delta ${number > 0 ? 'up' : 'down'}`}>
			<i className={`uil ${number > 0 ? 'uil-arrow-up' : 'uil-arrow-down'}`}></i>
			{decimal(Math.abs(number), 1)} {t('chain_pp')}
		</span>
	)
}

function CourierTracking() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [rankBy, setRankBy] = useState('sla_percent')
	const [search, setSearch] = useState('')
	const [showSmall, setShowSmall] = useState(false)
	const [details, setDetails] = useState(null)
	const [cheque, setCheque] = useState({})
	const [showRightBar, setShowRightBar] = useState(false)

	const filters = useChainFilters({ source_ids: [], sla_minutes: SLA_DEFAULT }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions(['sources'])

	async function searchWithFilter(filter) {
		const url = buildAnalyticsUrl('/chain/report/couriers', { ...filter, sla_minutes: clampSla(filter.sla_minutes) })
		const response = await GET(url, {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const sla = data?.sla_minutes || SLA_DEFAULT
	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const { sorted, sort, toggle } = useSort(branches, 'deliveries')

	// Рейтинг филиалов — только где своими курьерами есть замеренные доставки
	const rankGoodHigh = rankBy !== 'median_minutes'
	const { ranks, count } = useMemo(() => buildRanks(branches.filter(item => item.timed > 0), rankBy, rankGoodHigh), [branches, rankBy, rankGoodHigh])

	const stats = useMemo(() => ({
		median_minutes: columnStats(branches, 'median_minutes'),
		avg_minutes: columnStats(branches, 'avg_minutes'),
		p90_minutes: columnStats(branches, 'p90_minutes'),
		sla_percent: columnStats(branches, 'sla_percent'),
		avg_late_minutes: columnStats(branches, 'avg_late_minutes'),
		marked_percent: columnStats(branches, 'marked_percent'),
	}), [branches])
	const maxDeliveries = useMemo(() => columnStats(branches, 'deliveries').max, [branches])

	// Курьеры: поиск, отсечка по числу доставок, ранг по доле вовремя
	const couriers = useMemo(() => {
		const query = search.trim().toLowerCase()
		return (data?.couriers || []).filter(item => {
			if (!showSmall && item.deliveries < MIN_COURIER_DELIVERIES) return false
			if (!query) return true
			return `${item.courier_name} ${item.pos_names}`.toLowerCase().includes(query)
		})
	}, [data, search, showSmall])
	const hiddenSmall = (data?.couriers || []).filter(item => item.deliveries < MIN_COURIER_DELIVERIES).length
	const courierSort = useSort(couriers, 'deliveries')
	const courierRanks = useMemo(() => buildRanks(couriers.filter(item => item.timed > 0), 'sla_percent', true, 'courier_key'), [couriers])
	const courierStats = useMemo(() => ({
		median_minutes: columnStats(couriers, 'median_minutes'),
		sla_percent: columnStats(couriers, 'sla_percent'),
		avg_late_minutes: columnStats(couriers, 'avg_late_minutes'),
		marked_percent: columnStats(couriers, 'marked_percent'),
	}), [couriers])
	const maxCourierDeliveries = useMemo(() => columnStats(couriers, 'deliveries').max, [couriers])

	const timedBranches = branches.filter(item => item.timed > 0)
	const fastest = timedBranches.length > 1 ? [...timedBranches].sort((a, b) => b.sla_percent - a.sla_percent)[0] : null
	const slowest = timedBranches.length > 1 ? [...timedBranches].sort((a, b) => a.sla_percent - b.sla_percent)[0] : null

	const carrierLabels = {
		own: t('chain_carrier_own'),
		external: t('chain_carrier_external'),
		aggregator: t('chain_carrier_aggregator'),
		no_courier: t('chain_carrier_none'),
	}
	const onlyLabels = {
		late: t('chain_late_deliveries'),
		unmarked: t('chain_unmarked'),
		timed: t('chain_timed_deliveries'),
	}
	const rankLabels = {
		sla_percent: t('chain_on_time_share'),
		median_minutes: t('chain_median_time'),
		deliveries: t('chain_deliveries'),
	}

	async function openDetails({ posId = null, courier = null, carrier = null, only = null }) {
		const response = await GET(buildAnalyticsUrl('/chain/report/couriers-cheques', { ...applied, sla_minutes: sla }, {
			pos_id: posId,
			courier_key: courier ? courier.courier_key : null,
			carrier: carrier ? CARRIER_PARAM[carrier] : null,
			only,
		}), {}, { loader: true })
		if (httpOk(response)) {
			const branch = branches.find(item => item.pos_id === posId)
			const parts = []
			if (courier) parts.push(courier.courier_name)
			if (carrier) parts.push(carrierLabels[carrier])
			if (only) parts.push(onlyLabels[only])
			setDetails({
				...response.data,
				pos_name: branch ? branch.pos_name : t('chain_all_branches'),
				label: parts.length > 0 ? parts.join(' · ') : t('chain_deliveries'),
			})
		}
	}

	async function openCheque(row) {
		const response = await GET(`/cheques/${row.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setCheque({ ...response.data.data, showSidebar: true })
			setShowRightBar(true)
		}
	}

	function carrierName(row) {
		if (row.carrier === 'aggregator' && row.integration_name) return row.integration_name
		return carrierLabels[row.carrier === 'none' ? 'no_courier' : row.carrier]
	}

	const detailColumns = () => [
		{ label: t('cheque'), type: 'text', width: 10 },
		{ label: t('chain_branch'), type: 'text', width: 24 },
		{ label: t('courier'), type: 'text', width: 22 },
		{ label: t('chain_carrier'), type: 'text', width: 18 },
		{ label: t('client'), type: 'text', width: 18 },
		{ label: t('phone'), type: 'text', width: 15 },
		{ label: t('chain_opened_at'), type: 'text', width: 17 },
		{ label: t('chain_closed_at'), type: 'text', width: 17 },
		{ label: t('chain_delivery_minutes'), type: 'number', width: 11 },
		{ label: t('chain_late'), type: 'text', width: 9 },
		{ label: t('chain_marked_delivered_at'), type: 'text', width: 17 },
		{ label: t('amount'), type: 'money' },
	]
	const detailRow = (row) => [
		`№${row.cheque_number || row.id}`, row.pos_name, row.courier_name || '', carrierName(row), row.client_name || '', row.client_phone || '',
		formatDate(row.cheque_opened_at, 'dd.MM.yyyy HH:mm'), formatDate(row.cheque_closed_at, 'dd.MM.yyyy HH:mm'),
		row.kind === 'ok' ? row.minutes : '', row.is_late ? t('yes') : '',
		row.delivered_at ? formatDate(row.delivered_at, 'dd.MM.yyyy HH:mm') : '', row.amount,
	]

	const metricColumns = () => [
		{ label: t('chain_deliveries'), type: 'int', width: 10 },
		{ label: t('chain_carrier_own'), type: 'int', width: 11 },
		{ label: t('chain_carrier_external'), type: 'int', width: 11 },
		{ label: t('chain_carrier_aggregator'), type: 'int', width: 13 },
		{ label: t('chain_carrier_none'), type: 'int', width: 11 },
		{ label: t('chain_timed_deliveries'), type: 'int', width: 11 },
		{ label: t('chain_avg_time'), type: 'number', width: 10 },
		{ label: t('chain_median_time'), type: 'number', width: 10 },
		{ label: 'P90', type: 'number', width: 8 },
		{ label: `${t('chain_on_time_share')} (≤ ${sla})`, type: 'percent', width: 12 },
		{ label: t('chain_late_deliveries'), type: 'int', width: 10 },
		{ label: t('chain_avg_late'), type: 'number', width: 11 },
		{ label: t('chain_marked_share'), type: 'percent', width: 12 },
		{ label: t('chain_unmarked'), type: 'int', width: 12 },
		{ label: t('revenue'), type: 'money' },
	]
	const metricRow = (item) => [
		item.deliveries, item.own, item.external, item.aggregator, item.no_courier, item.timed,
		item.avg_minutes, item.median_minutes, item.p90_minutes, item.sla_percent, item.late, item.avg_late_minutes,
		item.marked_percent, item.unmarked, item.revenue,
	]

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)} · SLA ${sla} ${t('chain_min')}`
		const daily = data.daily || { days: [] }
		downloadExcel(`${t('chain_couriers_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('chain_branches'),
				title: t('chain_couriers_title'),
				subtitle: period,
				columns: [{ label: t('chain_branch'), type: 'text', width: 30 }, ...metricColumns()],
				rows: [...branches].sort((a, b) => b.deliveries - a.deliveries).map(item => [item.pos_name, ...metricRow(item)]),
				footer: [t('total'), ...metricRow(totals)],
			},
			{
				name: t('couriers'),
				title: `${t('chain_couriers_title')} — ${t('couriers')}`,
				subtitle: period,
				columns: [
					{ label: t('courier'), type: 'text', width: 26 },
					{ label: t('chain_branches'), type: 'text', width: 30 },
					...metricColumns(),
				],
				rows: (data.couriers || []).map(item => [item.courier_name, item.pos_names, ...metricRow(item)]),
			},
			{
				name: t('chain_by_days'),
				title: `${t('chain_couriers_title')} — ${t('chain_by_days')}`,
				subtitle: period,
				columns: [
					{ label: t('date'), type: 'text', width: 12 },
					{ label: t('chain_deliveries'), type: 'int', width: 10 },
					{ label: t('chain_median_time'), type: 'number', width: 10 },
					{ label: t('chain_on_time_share'), type: 'percent', width: 12 },
				],
				rows: daily.days.map((day, index) => [day.split('-').reverse().join('.'), daily.deliveries[index], daily.median_minutes[index], daily.sla_percent[index]]),
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('chain_couriers_title')} — ${details.pos_name} — ${details.label}`, [{
			name: t('chain_deliveries'),
			title: `${details.label} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)} · SLA ${details.sla_minutes} ${t('chain_min')}`,
			columns: detailColumns(),
			rows: details.rows.map(detailRow),
		}])
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_couriers_title')}
				icon="uil-truck"
				filters={filters}
				subtitle={`SLA ${sla} ${t('chain_min')}`}
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<StandardFields filters={filters} options={options} branch={false} orderType={false} />
				<FilterField label={t('chain_sla_minutes')}>
					<input
						type="number"
						className="form-control"
						min={SLA_MIN}
						max={SLA_MAX}
						value={draft.sla_minutes}
						title={t('chain_sla_hint')}
						onChange={(e) => patch({ sla_minutes: e.target.value })}
						onBlur={() => patch({ sla_minutes: clampSla(draft.sla_minutes) })}
						style={{ borderRadius: '10px', height: '38px', borderColor: 'var(--dark-border, #dfe5ec)' }}
					/>
				</FilterField>
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_deliveries')} icon="uil-truck" color="#5b73e8"
						value={formatMoney(totals.deliveries || 0)}
						sub={<><Delta value={totals.deliveries_delta} /> {t('chain_vs_prev')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_carrier_own')} icon="uil-user-check" color="#7c4dff"
						value={percent(totals.own_percent)}
						sub={<>{formatMoney(totals.own || 0)} · {t('chain_carrier_aggregator')} {formatMoney(totals.aggregator || 0)}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_median_time')} icon="uil-clock" color="#f59f00" title2={t('chain_delivery_time_hint')}
						value={totals.median_minutes !== null && totals.median_minutes !== undefined ? `${minutes(totals.median_minutes)} ${t('chain_min')}` : '—'}
						sub={<><Delta value={totals.median_delta} goodHigh={false} /> · {t('chain_avg_time')} {minutes(totals.avg_minutes)}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={`${t('chain_on_time_share')} ≤ ${sla}`} icon="uil-check-circle" color="#12b886"
						value={percent(totals.sla_percent)}
						sub={<><PointsDelta value={totals.sla_diff} /> {t('chain_vs_prev')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_late_deliveries')} icon="uil-stopwatch-slash" color="#f06548"
						value={formatMoney(totals.late || 0)}
						sub={<>{t('chain_avg_late')} <b>{minutes(totals.avg_late_minutes)}</b> {t('chain_min')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_unmarked')} icon="uil-map-marker-question" color="#adb5bd" title2={t('chain_unmarked_hint')}
						value={formatMoney(totals.unmarked || 0)}
						sub={<>{t('chain_marked_share')} <b>{percent(totals.marked_percent)}</b></>} />
				</div>
			</div>

			<div className="chain-note mb-3">
				<i className="uil uil-info-circle"></i>
				<span>{t('chain_couriers_note').replace(':sla', sla)}</span>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_rating')}</h5>
							<span className="chain-muted">{t('chain_rank_by')}</span>
							<FilterSegment
								options={['sla_percent', 'median_minutes', 'deliveries'].map(id => ({ id, label: rankLabels[id] }))}
								value={rankBy}
								onChange={setRankBy}
							/>
						</div>
						<HeatLegend />
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
									<SortTh sortKey="deliveries" sort={sort} onSort={toggle} className="text-end">{t('chain_deliveries')}</SortTh>
									<th style={{ minWidth: '170px' }}>{t('chain_carrier')}</th>
									<SortTh sortKey="median_minutes" sort={sort} onSort={toggle} className="text-end" title={t('chain_delivery_time_hint')}>{t('chain_median_time')}</SortTh>
									<SortTh sortKey="avg_minutes" sort={sort} onSort={toggle} className="text-end">{t('chain_avg_time')}</SortTh>
									<SortTh sortKey="p90_minutes" sort={sort} onSort={toggle} className="text-end" title={t('chain_p90_hint')}>P90</SortTh>
									<SortTh sortKey="sla_percent" sort={sort} onSort={toggle} className="text-end">{t('chain_on_time_share')}</SortTh>
									<SortTh sortKey="late" sort={sort} onSort={toggle} className="text-end">{t('chain_late_deliveries')}</SortTh>
									<SortTh sortKey="avg_late_minutes" sort={sort} onSort={toggle} className="text-end" title={t('chain_avg_late_hint')}>{t('chain_avg_late')}</SortTh>
									<SortTh sortKey="marked_percent" sort={sort} onSort={toggle} className="text-end" title={t('chain_unmarked_hint')}>{t('chain_marked_share')}</SortTh>
									<SortTh sortKey="revenue" sort={sort} onSort={toggle} className="text-end">{t('revenue')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={12} />}
								{sorted.map(item => {
									const index = branches.indexOf(item)
									return (
										<tr key={item.pos_id}>
											<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
											<td className="chain-name chain-sticky-2">
												<span className="d-inline-flex align-items-center">
													<span className="chain-dot" style={{ background: chainColor(index) }}></span>
													{item.pos_name}
												</span>
											</td>
											<FillCell percent={maxDeliveries > 0 ? item.deliveries / maxDeliveries * 100 : 0} tone="primary"
												onClick={item.deliveries > 0 ? () => openDetails({ posId: item.pos_id }) : undefined}>
												<b>{formatMoney(item.deliveries)}</b>
												<div><Delta value={item.deliveries_delta} showNone={false} /></div>
											</FillCell>
											<td><CarrierSplit row={item} labels={carrierLabels} onPick={(carrier) => openDetails({ posId: item.pos_id, carrier })} /></td>
											<td className="chain-num" style={heatStyle(item.median_minutes, stats.median_minutes, false)}>
												<b>{minutes(item.median_minutes)}</b>
												<div><Delta value={item.median_delta} goodHigh={false} showNone={false} /></div>
											</td>
											<td className="chain-num" style={heatStyle(item.avg_minutes, stats.avg_minutes, false)}>{minutes(item.avg_minutes)}</td>
											<td className="chain-num" style={heatStyle(item.p90_minutes, stats.p90_minutes, false)}>{minutes(item.p90_minutes)}</td>
											<td className="chain-num" style={heatStyle(item.sla_percent, stats.sla_percent)}
												title={`${t('chain_timed_deliveries')}: ${formatMoney(item.timed)}`}>
												<b>{percent(item.sla_percent)}</b>
												<div><PointsDelta value={item.sla_diff} /></div>
											</td>
											<td className={`chain-num ${item.late > 0 ? 'chain-click' : ''}`}
												onClick={() => item.late > 0 && openDetails({ posId: item.pos_id, only: 'late' })}>
												<span>{item.timed > 0 ? formatMoney(item.late) : '—'}</span>
												{item.timed > 0 && <div className="chain-muted">{percent(item.late_percent)}</div>}
											</td>
											<td className="chain-num" style={heatStyle(item.avg_late_minutes, stats.avg_late_minutes, false)}>{minutes(item.avg_late_minutes)}</td>
											<td className={`chain-num ${item.unmarked > 0 ? 'chain-click' : ''}`} style={heatStyle(item.marked_percent, stats.marked_percent)}
												onClick={() => item.unmarked > 0 && openDetails({ posId: item.pos_id, only: 'unmarked' })}
												title={item.own > 0 ? `${t('chain_unmarked')}: ${formatMoney(item.unmarked)}` : undefined}>
												<span>{percent(item.marked_percent)}</span>
												{item.unmarked > 0 && <div className="chain-muted">{formatMoney(item.unmarked)} {t('chain_without_mark')}</div>}
											</td>
											<td className="chain-num">{money(item.revenue)}</td>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										<td className="chain-num chain-click" onClick={() => totals.deliveries > 0 && openDetails({})}>{formatMoney(totals.deliveries)}</td>
										<td><CarrierSplit row={totals} labels={carrierLabels} onPick={(carrier) => openDetails({ carrier })} /></td>
										<td className="chain-num">{minutes(totals.median_minutes)}</td>
										<td className="chain-num">{minutes(totals.avg_minutes)}</td>
										<td className="chain-num">{minutes(totals.p90_minutes)}</td>
										<td className="chain-num">{percent(totals.sla_percent)}</td>
										<td className="chain-num chain-click" onClick={() => totals.late > 0 && openDetails({ only: 'late' })}>{formatMoney(totals.late)}</td>
										<td className="chain-num">{minutes(totals.avg_late_minutes)}</td>
										<td className="chain-num chain-click" onClick={() => totals.unmarked > 0 && openDetails({ only: 'unmarked' })}>{percent(totals.marked_percent)}</td>
										<td className="chain-num">{money(totals.revenue)}</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					{fastest && slowest && fastest.pos_id !== slowest.pos_id && (
						<div className="chain-muted mt-2">
							<i className="uil uil-info-circle me-1"></i>
							{t('chain_best_branch')}: <b>{fastest.pos_name}</b> ({percent(fastest.sla_percent)} {t('chain_on_time_lower')})
							· {t('chain_worst_branch')}: <b>{slowest.pos_name}</b> ({percent(slowest.sla_percent)})
						</div>
					)}
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_couriers_rating')}</h5>
							<span className="chain-muted">{t('chain_rank_by_on_time')}</span>
						</div>
						<div className="chain-toolbar-group">
							{hiddenSmall > 0 && (
								<label className="chain-check">
									<input type="checkbox" checked={showSmall} onChange={(e) => setShowSmall(e.target.checked)} />
									{t('chain_show_small_couriers').replace(':count', MIN_COURIER_DELIVERIES)} ({hiddenSmall})
								</label>
							)}
							<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_courier')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="courier_name" sort={courierSort.sort} onSort={courierSort.toggle} className="chain-sticky-2">{t('courier')}</SortTh>
									<SortTh sortKey="pos_names" sort={courierSort.sort} onSort={courierSort.toggle}>{t('chain_branches')}</SortTh>
									<SortTh sortKey="deliveries" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end">{t('chain_deliveries')}</SortTh>
									<SortTh sortKey="median_minutes" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end">{t('chain_median_time')}</SortTh>
									<SortTh sortKey="avg_minutes" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end">{t('chain_avg_time')}</SortTh>
									<SortTh sortKey="sla_percent" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end">{t('chain_on_time_share')}</SortTh>
									<SortTh sortKey="late" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end">{t('chain_late_deliveries')}</SortTh>
									<SortTh sortKey="avg_late_minutes" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end" title={t('chain_avg_late_hint')}>{t('chain_avg_late')}</SortTh>
									<SortTh sortKey="marked_percent" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end" title={t('chain_unmarked_hint')}>{t('chain_marked_share')}</SortTh>
									<SortTh sortKey="revenue" sort={courierSort.sort} onSort={courierSort.toggle} className="text-end">{t('revenue')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{courierSort.sorted.length === 0 && <ChainEmpty colSpan={11} />}
								{courierSort.sorted.map(item => (
									<tr key={item.courier_key}>
										<td className="chain-index chain-sticky"><RankBadge rank={courierRanks.ranks[item.courier_key]} count={courierRanks.count} /></td>
										<td className="chain-name chain-sticky-2">
											{item.courier_name}
											{item.is_external && <div className="chain-muted">{t('chain_carrier_external')}</div>}
										</td>
										<td className="chain-muted" style={{ maxWidth: '220px', whiteSpace: 'normal' }}>{item.pos_names || '—'}</td>
										<FillCell percent={maxCourierDeliveries > 0 ? item.deliveries / maxCourierDeliveries * 100 : 0} tone="primary"
											onClick={() => openDetails({ courier: item })}>
											<b>{formatMoney(item.deliveries)}</b>
										</FillCell>
										<td className="chain-num" style={heatStyle(item.median_minutes, courierStats.median_minutes, false)}>{minutes(item.median_minutes)}</td>
										<td className="chain-num">{minutes(item.avg_minutes)}</td>
										<td className="chain-num" style={heatStyle(item.sla_percent, courierStats.sla_percent)}
											title={`${t('chain_timed_deliveries')}: ${formatMoney(item.timed)}`}>
											<b>{percent(item.sla_percent)}</b>
										</td>
										<td className={`chain-num ${item.late > 0 ? 'chain-click' : ''}`}
											onClick={() => item.late > 0 && openDetails({ courier: item, only: 'late' })}>
											<span>{item.timed > 0 ? formatMoney(item.late) : '—'}</span>
										</td>
										<td className="chain-num" style={heatStyle(item.avg_late_minutes, courierStats.avg_late_minutes, false)}>{minutes(item.avg_late_minutes)}</td>
										<td className={`chain-num ${item.unmarked > 0 ? 'chain-click' : ''}`} style={heatStyle(item.marked_percent, courierStats.marked_percent)}
											onClick={() => item.unmarked > 0 && openDetails({ courier: item, only: 'unmarked' })}>
											<span>{percent(item.marked_percent)}</span>
											{item.unmarked > 0 && <div className="chain-muted">{formatMoney(item.unmarked)} {t('chain_without_mark')}</div>}
										</td>
										<td className="chain-num">{money(item.revenue)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<DailyChart daily={data?.daily} sla={sla} />

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.label} — {details?.pos_name}
						<div className="chain-muted fw-normal">
							{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_deliveries_lower')} · SLA {details?.sla_minutes} {t('chain_min')}
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>{t('cheque')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('courier')}</th>
								<th>{t('client')}</th>
								<th>{t('chain_opened_at')}</th>
								<th>{t('chain_closed_at')}</th>
								<th className="text-end">{t('chain_delivery_minutes')}</th>
								<th title={t('chain_unmarked_hint')}>{t('delivered')}</th>
								<th className="text-end">{t('amount')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={9} />}
							{(details?.rows || []).map(row => (
								<tr key={row.id}>
									<td className="chain-click text-nowrap" onClick={() => openCheque(row)} title={t('chain_open_cheque')}>
										<span className="text-primary">№{row.cheque_number || row.id}</span>
									</td>
									<td>{row.pos_name}</td>
									<td>
										{row.courier_name || '—'}
										<div className="chain-muted">{carrierName(row)}</div>
									</td>
									<td className="text-nowrap">
										{row.client_name || '—'}
										{row.client_phone && row.client_phone !== row.client_name && <div className="chain-muted">{row.client_phone}</div>}
									</td>
									<td className="text-nowrap">{formatDate(row.cheque_opened_at, 'dd.MM.yy HH:mm')}</td>
									<td className="text-nowrap">{formatDate(row.cheque_closed_at, 'dd.MM.yy HH:mm')}</td>
									<td className="chain-num" style={row.is_late ? { color: '#f06548', fontWeight: 700 } : undefined}
										title={row.kind !== 'ok' ? t(`chain_time_kind_${row.kind}`) : undefined}>
										{row.kind === 'ok' ? minutes(row.minutes) : <span className="chain-muted">{minutes(row.minutes)}*</span>}
									</td>
									<td className="text-nowrap">
										{row.delivered_at ? formatDate(row.delivered_at, 'HH:mm') : row.carrier === 'own' ? <span className="chain-muted">{t('chain_no_mark')}</span> : '—'}
									</td>
									<td className="chain-num">{money(row.amount)}</td>
								</tr>
							))}
						</tbody>
					</table>
					<div className="chain-muted mt-2">* {t('chain_time_excluded_hint')}</div>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>

			<Rightbar showRightBar={showRightBar} setShowRightBar={setShowRightBar} selectedItem={cheque} />
		</>
	)
}

// Полоса «кто вёз»: свой курьер / служба доставки / агрегатор / без курьера
function CarrierSplit({ row, labels, onPick }) {
	const total = row.deliveries || 0
	if (total === 0) return <span className="chain-muted">—</span>
	const title = CARRIERS.map(key => `${labels[key]}: ${formatMoney(row[key] || 0)}`).join('\n')
	return (
		<div title={title}>
			<div className="chain-split">
				{CARRIERS.map(key => row[key] > 0 && (
					<span key={key} className="chain-click" onClick={() => onPick(key)}
						style={{ width: `${row[key] / total * 100}%`, background: CARRIER_COLORS[key] }}></span>
				))}
			</div>
			<div className="chain-muted mt-1 text-nowrap">
				{CARRIERS.filter(key => row[key] > 0).map(key => (
					<span key={key} className="me-2">
						<span className="chain-dot" style={{ background: CARRIER_COLORS[key] }}></span>{percent(row[key] / total * 100, 0)}
					</span>
				))}
			</div>
		</div>
	)
}

// Медиана по дням и доля вовремя
function DailyChart({ daily, sla }) {
	const { t } = useTranslation()
	const days = daily?.days || []
	if (days.length < 2) return null
	const series = [
		{ name: t('chain_deliveries'), type: 'column', data: daily.deliveries },
		{ name: t('chain_median_time'), type: 'line', data: daily.median_minutes },
		{ name: t('chain_on_time_share'), type: 'line', data: daily.sla_percent },
	]
	const options = {
		chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
		colors: ['rgba(91, 115, 232, 0.45)', '#f59f00', '#12b886'],
		stroke: { width: [0, 3, 3], curve: 'smooth' },
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: {
			categories: days.map(day => day.slice(8, 10) + '.' + day.slice(5, 7)),
			tickAmount: Math.min(days.length, 15),
			labels: { rotate: 0, hideOverlappingLabels: true },
		},
		yaxis: [
			{ seriesName: t('chain_deliveries'), labels: { formatter: value => formatMoney(Math.round(value)) } },
			{ seriesName: t('chain_median_time'), opposite: true, labels: { formatter: value => value === null || value === undefined ? '' : `${Math.round(value)} ${t('chain_min')}` } },
			{ seriesName: t('chain_on_time_share'), opposite: true, min: 0, max: 100, labels: { formatter: value => value === null || value === undefined ? '' : `${Math.round(value)}%` } },
		],
		tooltip: { shared: true },
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{t('chain_by_days')}</h5>
					<span className="chain-muted">SLA {sla} {t('chain_min')}</span>
				</div>
				<ReactApexChart type="line" height={340} series={series} options={options} />
			</div>
		</div>
	)
}

export default CourierTracking
